import { FtIn } from './ft-in.js';
/**
 * Stirrup/tie hook extension per ACI 318 §25.3.
 * Reference: CRSI Standard Stirrup/Tie Hook Details quick-ref card (#3-#8).
 *
 *   90° standard: max(12 × db, 6")  — ACI 318 §25.3.2 for ties/stirrups
 *   135° seismic: max(6 × db, 3")   — ACI 318 §25.3.3 (seismic hoops)
 *
 * Returns extension in INCHES (decimal). Caller converts to FtIn / decimal
 * feet as needed.
 */
export function getStirrupHookExtensionInches(barSize, hookType) {
    const db = barSize.nominalDiameter;
    switch (hookType) {
        case 'standard_90':
            return Math.max(12 * db, 6);
        case 'seismic_135':
            return Math.max(6 * db, 3);
        case 'soft':
            // Soft hooks are typically interpreted same as 90° but without the
            // tail-extension requirement — fall back to 90° formula until the
            // engine grows a deliberate model.
            return Math.max(12 * db, 6);
    }
}
/**
 * Per-tie bar length for a rectangular tie (function codes TA / TB / TD).
 *
 * Treats L1 and L2 as the two leg dimensions of the rebar tie at centerline,
 * i.e. the caller has already subtracted concrete clearance, OR L1 and L2
 * are authored as steel dimensions directly.
 *
 *   length = 2 × (L1 + L2) + 2 × hookExtension(barSize, hookType)
 *
 * For TB Double Tie the diagonal must be added separately by the caller. For
 * TD Triple Tie the middle crossbar must be added (use
 * `computeTripleTieCrossbarLength`).
 */
export function computeRectangularTieLength(L1, L2, barSize, hookType) {
    const perimeterInches = 2 * (L1.toDecimalInches() + L2.toDecimalInches());
    const hookInches = 2 * getStirrupHookExtensionInches(barSize, hookType);
    return FtIn.fromDecimalInches(perimeterInches + hookInches);
}
/**
 * Inscribed diamond perimeter for a TB Double Tie. The aSa Double Tie is a
 * "square and diamond" set: an outer rectangle plus a diamond (square rotated
 * 45°) inscribed at the rectangle's edge midpoints.
 *
 * For a rectangle with edges L1 and L2, the inscribed diamond has corners at
 * each edge's midpoint. Each diamond side connects two adjacent midpoints,
 * forming a right triangle with legs (L1/2, L2/2):
 *
 *   side = √((L1/2)² + (L2/2)²)
 *   perimeter = 4 × side
 *
 * Plus 2 × hook extension where the diamond bar terminates.
 *
 * Validated against the E26-1660 TB sample: 25 ties × (rect 9 ft + diamond
 * 6.66 ft) × 0.668 ≈ 304 lb (matches aSa printed).
 */
export function computeInscribedDiamondLength(L1, L2, barSize, hookType) {
    const halfL1 = L1.toDecimalInches() / 2;
    const halfL2 = L2.toDecimalInches() / 2;
    const sideInches = Math.sqrt(halfL1 * halfL1 + halfL2 * halfL2);
    const perimeterInches = 4 * sideInches;
    const hookInches = 2 * getStirrupHookExtensionInches(barSize, hookType);
    return FtIn.fromDecimalInches(perimeterInches + hookInches);
}
/**
 * Crossbar length for a TD Triple Tie (the middle bar that bisects the
 * rectangle along its long dimension). One end terminates with a hook into
 * the closed perimeter; the other end likewise. Total per-set length is
 * `computeRectangularTieLength(...) + computeTripleTieCrossbarLength(...)`.
 *
 *   length = (L1 − 2 × clearance) + 2 × hookExtension(barSize, hookType)
 *
 * Per the aSa Triple Tie diagram (`L1 − 2(CLR)` annotation in the data form),
 * the crossbar spans the inside of the perimeter so it's shorter than L1 by
 * 2 × clearance. Matches Form #011 conventions for a rectangle-plus-middle
 * crossbar tie pattern.
 */
export function computeTripleTieCrossbarLength(L1, clearance, barSize, hookType) {
    const innerInches = L1.toDecimalInches() - 2 * clearance.toDecimalInches();
    const hookInches = 2 * getStirrupHookExtensionInches(barSize, hookType);
    return FtIn.fromDecimalInches(innerInches + hookInches);
}
/**
 * Compute square tie L1/L2/L3 dimensions for a beam.
 *
 * L1 = beamWidth − 2 × cover  (out-to-out width minus both covers)
 * L2 = beamDepth − 2 × cover  (out-to-out depth minus both covers)
 * L3 = hook extension length
 *
 * 90° standard hook (this phase): L3 = max(12 × tieBarDia, 6 inches) per ACI 318.
 * soft / seismic_135: throws — Phase 3.
 */
export function computeSquareTieDimensions(input) {
    if (input.hookType !== 'standard_90') {
        throw new Error(`hookType '${input.hookType}' not yet implemented in this phase`);
    }
    const twiceCover = input.cover.multiply(2);
    const L1 = input.beamWidth.subtract(twiceCover);
    const L2 = input.beamDepth.subtract(twiceCover);
    const hookByDia = input.tieBarSize.nominalDiameter * 12;
    const L3 = FtIn.fromDecimalInches(Math.max(hookByDia, 6));
    return { L1, L2, L3 };
}
/**
 * Compute total bar length for a single circular tie.
 *
 * effectiveDiameter = columnDiameter − 2 × cover − tieBarDia
 * circumference     = π × effectiveDiameter
 *
 * Closure variants:
 *   - seismic_135: length = circumference + 2 × max(6 × tieBarDia, 3 in)
 *   - lap:         length = circumference + lap   (hooks omitted; lap is the
 *                  bar-on-bar overlap that closes the hoop, common in
 *                  non-seismic pier-tie work — matches the aSa Circular Tie
 *                  function entry where Lap is field L5)
 *   - standard_90: throws — not yet implemented
 */
export function computeCircleTieLength(input) {
    const dia = input.tieBarSize.nominalDiameter;
    const effectiveDiaInches = input.columnDiameter.subtract(input.cover.multiply(2)).toDecimalInches() - dia;
    const circumferenceInches = Math.PI * effectiveDiaInches;
    switch (input.closureType) {
        case 'seismic_135': {
            const hookExtensionInches = Math.max(6 * dia, 3);
            return FtIn.fromDecimalInches(circumferenceInches + 2 * hookExtensionInches);
        }
        case 'lap': {
            if (!input.lap) {
                throw new Error("closureType 'lap' requires a `lap` length input");
            }
            return FtIn.fromDecimalInches(circumferenceInches + input.lap.toDecimalInches());
        }
        case 'standard_90':
            throw new Error(`closureType 'standard_90' not yet implemented in this phase`);
    }
}
